class ThreeSixtyModal extends HTMLElement {
  constructor() {
    super();
    this.modal = null;
    this.modalContent = null;
    this.openButton = null;
    this.closeButton = null;
    this.sirvContainer = null;
    this.productAdd = null;
    this.cartAddHandler = null;
    this.cartErrorHandler = null;
    this.variantChangeUnsubscriber = null;
    this.boundResizeHandler = null;
    this.swymObserver = null;
    this.swymOriginalParent = null;
    this.swymOriginalNextSibling = null;
    this.usesTopLayerDialog = false;
    this.sectionRoot = null;
    this.boundDelegatedOpenHandler = null;
    this.bodyScrollState = null;
    this.boundVisualViewportHandler = null;
    this.suppressModalVariantSync = false;
    this.suppressMainVariantSync = false;
    this.variantSirvMap = {};
    this.fallbackSirvUrl = '';
  }

  connectedCallback() {
    this.modal = this.querySelector('.modal__container');
    this.modalContent = this.querySelector('.modal__content');
    this.openButton = this.querySelector('.modal__opener');
    this.closeButton = this.querySelector('.modal__close');
    this.sirvContainer = this.querySelector('.Sirv');
    this.productAdd = this.querySelector('.product__add');
    this.fallbackSirvUrl = (this.sirvContainer?.dataset.productSirvUrl || '').trim();
    this.variantSirvMap = this.getVariantSirvMap();

    this.sectionRoot = this.getSectionRoot(this);
    this.boundDelegatedOpenHandler = (event) => {
      const trigger = event.target.closest('.js-three-sixty-modal-trigger');
      if (!trigger) return;
      if (this.contains(trigger)) return;
      if (this.getSectionRoot(trigger) !== this.sectionRoot) return;

      event.preventDefault();
      this.open();
    };

    this.sectionRoot.addEventListener('click', this.boundDelegatedOpenHandler);

    if (this.openButton && this.modal) {
      this.openButton.addEventListener('click', () => this.open());
    }

    if (this.closeButton && this.modal) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    if (this.productAdd && this.modal) {
      this.productAdd.addEventListener('click', () => this.handleAddToCart());
    }

    if (this.modal) {
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) {
          this.close();
        }
      });

      this.modal.addEventListener('cancel', () => {
        this.close();
      });
    }

    this.boundResizeHandler = () => {
      if (this.modal.hasAttribute('open')) {
        this.setModalPosition();
      }
    };

    window.addEventListener('resize', this.boundResizeHandler);

    this.boundVisualViewportHandler = () => {
      if (this.modal && this.modal.hasAttribute('open')) {
        this.setModalPosition();
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.boundVisualViewportHandler);
      window.visualViewport.addEventListener('scroll', this.boundVisualViewportHandler);
    }

    /* Listen for cart events */
    this.cartAddHandler = (e) => {
      if (this.waitingForCart) {
        this.waitingForCart = false;
        this.close();
      }
    };

    this.cartErrorHandler = (e) => {
      if (this.waitingForCart) {
        this.waitingForCart = false;
        if (this.productAdd) {
          this.productAdd.disabled = false;
          this.productAdd.textContent = this.productAdd.dataset.originalText || 'ADD TO BAG';
        }
      }
    };

    document.addEventListener('cart:item-added', this.cartAddHandler);
    document.addEventListener('cart:item-error', this.cartErrorHandler);

    /* Subscribe to variant changes from main product form */
    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      this.syncVariantFromMain(event.data);
    });

    /* Listen for variant changes within modal */
    const modalVariantRadios = this.querySelector('variant-radios');
    if (modalVariantRadios) {
      modalVariantRadios.addEventListener('change', (e) => {
        if (this.suppressModalVariantSync || e.__pairSync) return;
        this.syncVariantToMain(e);
      });
    }

    this.updateSirvSource(this.getCurrentVariantId());
  }

  getVariantSirvMap() {
    const mapScript = this.querySelector('.three-sixty-variant-sirv-map');
    if (!mapScript) return {};

    try {
      const parsed = JSON.parse(mapScript.textContent || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  getCurrentVariantId() {
    const modalVariantInput = this.querySelector('.product-variant-id');
    if (modalVariantInput && modalVariantInput.value) return String(modalVariantInput.value);

    const selectedModalVariant = this.querySelector('variant-radios input[type="radio"]:checked');
    if (selectedModalVariant && selectedModalVariant.value) return String(selectedModalVariant.value);

    const mainVariantInput = document.querySelector('product-info form input[name="id"]');
    if (mainVariantInput && mainVariantInput.value) return String(mainVariantInput.value);

    return null;
  }

  withSirvViewerParams(sourceUrl) {
    const rawUrl = String(sourceUrl || '').trim();
    if (!rawUrl) return '';

    try {
      const parsedUrl = new URL(rawUrl, window.location.origin);
      parsedUrl.searchParams.set('autospin', 'once');
      parsedUrl.searchParams.set('fullscreen', 'false');
      parsedUrl.searchParams.set('speed', '75');
      parsedUrl.searchParams.set('hint', 'false');
      return parsedUrl.toString();
    } catch (error) {
      const separator = rawUrl.includes('?') ? '&' : '?';
      return `${rawUrl}${separator}autospin=once&fullscreen=false&speed=75&hint=false`;
    }
  }

  reinitializeSirvViewer(nextSrc) {
    if (!this.sirvContainer) return;

    const currentInstance = window.Sirv?.getInstance?.(this.sirvContainer);
    currentInstance?.destroy?.();

    const nextContainer = this.sirvContainer.cloneNode(false);
    nextContainer.setAttribute('data-src', nextSrc);

    this.sirvContainer.replaceWith(nextContainer);
    this.sirvContainer = nextContainer;

    if (window.Sirv) {
      window.Sirv.start(this.sirvContainer);
    }
  }

  updateSirvSource(variantId) {
    if (!this.sirvContainer) return;

    const normalizedVariantId = variantId ? String(variantId) : null;
    const variantUrl = normalizedVariantId ? this.variantSirvMap[normalizedVariantId] : null;
    const sourceUrl = String(variantUrl || this.fallbackSirvUrl || '').trim();
    if (!sourceUrl) return;

    const nextSrc = this.withSirvViewerParams(sourceUrl);
    const currentSrc = this.sirvContainer.getAttribute('data-src');

    if (currentSrc === nextSrc) return;

    this.reinitializeSirvViewer(nextSrc);
  }

  syncVariantFromMain(data) {
    /* Update modal variant picker when main form changes */
    const modalVariantRadios = this.querySelector('variant-radios');
    if (!modalVariantRadios) return;

    const modalInputs = modalVariantRadios.querySelectorAll('input[type="radio"]');
    
    if (data.variant && data.variant.id) {
      this.suppressModalVariantSync = true;
      this.updateSirvSource(data.variant.id);

      modalInputs.forEach(input => {
        if (input.value == data.variant.id) {
          input.checked = true;
        }
      });

      /* Update price in modal */
      const modalPrice = this.querySelector('.modal__price');
      if (modalPrice && data.html) {
        const newPrice = data.html.querySelector('.product__price');
        if (newPrice) {
          modalPrice.innerHTML = newPrice.innerHTML;
        }
      }

      /* Update variant input */
      const modalVariantInput = this.querySelector('.product-variant-id');
      if (modalVariantInput) {
        modalVariantInput.value = data.variant.id;
      }

      queueMicrotask(() => {
        this.suppressModalVariantSync = false;
      });
    }
  }

  syncVariantToMain(event) {
    /* Don't sync if change came from main form */
    if (event.detail && event.detail.fromMain) return;
    if (event.__pairSync || this.suppressMainVariantSync) return;

    const selectedVariantId = event.target.value;
    if (!selectedVariantId) return;

    this.updateSirvSource(selectedVariantId);

    const mainProductInfo = document.querySelector('product-info');
    if (!mainProductInfo) return;

    const mainVariantRadios = mainProductInfo.querySelector('variant-radios');
    if (!mainVariantRadios) return;

    const mainInputs = mainVariantRadios.querySelectorAll('input[type="radio"]');

    this.suppressMainVariantSync = true;

    mainInputs.forEach(input => {
      if (input.value == selectedVariantId) {
        if (!input.checked) {
          input.checked = true;
          /* Trigger change event to update main product */
          const changeEvent = new Event('change', { bubbles: true });
          changeEvent.__threeSixtySync = true;
          input.dispatchEvent(changeEvent);
        }
      }
    });

    queueMicrotask(() => {
      this.suppressMainVariantSync = false;
    });
  }

  handleAddToCart() {
    if (!this.productAdd) return;

    /* Set flag to wait for cart event */
    this.waitingForCart = true;

    /* Set a timeout as fallback in case event doesn't fire */
    setTimeout(() => {
      if (this.waitingForCart) {
        this.waitingForCart = false;
        this.close();
      }
    }, 3000);
  }

  setModalPosition() {
    if (window.innerWidth < 1366) {
      const announcementBar = document.querySelector('.announcement-bar');
      const headerGroup = document.querySelector('.shopify-section-group-header-group .header-wrapper');
      const viewportHeight = window.visualViewport
        ? Math.round(window.visualViewport.height)
        : window.innerHeight;
      
      let topOffset = 0;
      
      if (announcementBar) {
        topOffset += announcementBar.offsetHeight;
      }
      
      if (headerGroup) {
        topOffset += headerGroup.offsetHeight;
      }

      topOffset += 15;
      const availableHeight = Math.max(viewportHeight - topOffset, 0);
      
      this.modal.style.setProperty('top', `${topOffset}px`);
      this.modal.style.setProperty('height', `${availableHeight}px`);
      this.modal.style.setProperty('max-height', `${availableHeight}px`);
    } else {
      this.modal.style.removeProperty('top');
      this.modal.style.removeProperty('height');
      this.modal.style.removeProperty('max-height');
    }
  }

  isMobileViewport() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  getSectionRoot(element) {
    return element.closest('.shopify-section') || document.body;
  }

  moveSwymNotificationsIntoModal() {
    if (!this.modal || !this.usesTopLayerDialog) return;

    const swymContainer = document.querySelector('.swym-notifications-container');
    if (!swymContainer) return;

    if (!this.swymOriginalParent && swymContainer.parentElement) {
      this.swymOriginalParent = swymContainer.parentElement;
      this.swymOriginalNextSibling = swymContainer.nextElementSibling;
    }

    if (swymContainer.parentElement !== this.modal) {
      this.modal.appendChild(swymContainer);
    }

    swymContainer.classList.add('swym-in-three-sixty-modal');
  }

  restoreSwymNotificationsContainer() {
    const swymContainer = document.querySelector('.swym-notifications-container');
    if (!swymContainer) return;

    swymContainer.classList.remove('swym-in-three-sixty-modal');

    if (this.swymOriginalParent && swymContainer.parentElement !== this.swymOriginalParent) {
      if (
        this.swymOriginalNextSibling
        && this.swymOriginalNextSibling.parentElement === this.swymOriginalParent
      ) {
        this.swymOriginalParent.insertBefore(swymContainer, this.swymOriginalNextSibling);
      } else {
        this.swymOriginalParent.appendChild(swymContainer);
      }
    }

    this.swymOriginalParent = null;
    this.swymOriginalNextSibling = null;
  }

  setupSwymObserver() {
    this.disconnectSwymObserver();

    if (!this.modal || !this.usesTopLayerDialog || !this.modal.hasAttribute('open')) return;

    this.swymObserver = new MutationObserver(() => {
      if (this.modal.hasAttribute('open') && this.isMobileViewport()) {
        this.moveSwymNotificationsIntoModal();
      }
    });

    this.swymObserver.observe(document.body, { childList: true, subtree: true });
  }

  disconnectSwymObserver() {
    if (this.swymObserver) {
      this.swymObserver.disconnect();
      this.swymObserver = null;
    }
  }

  lockPageScroll() {
    if (this.bodyScrollState) return;

    const scrollY = window.scrollY || window.pageYOffset || 0;

    this.bodyScrollState = {
      scrollY,
      bodyPosition: document.body.style.position,
      bodyTop: document.body.style.top,
      bodyLeft: document.body.style.left,
      bodyRight: document.body.style.right,
      bodyWidth: document.body.style.width,
      bodyOverflow: document.body.style.overflow,
      htmlOverflow: document.documentElement.style.overflow
    };

    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    document.body.style.scrollbarGutter = 'stable';
    document.documentElement.style.overflow = 'hidden';
  }

  unlockPageScroll() {
    if (!this.bodyScrollState) return;

    const { scrollY } = this.bodyScrollState;

    document.body.style.position = this.bodyScrollState.bodyPosition;
    document.body.style.top = this.bodyScrollState.bodyTop;
    document.body.style.left = this.bodyScrollState.bodyLeft;
    document.body.style.right = this.bodyScrollState.bodyRight;
    document.body.style.width = this.bodyScrollState.bodyWidth;
    document.body.style.overflow = this.bodyScrollState.bodyOverflow;
    document.body.style.scrollbarGutter = '';
    document.documentElement.style.overflow = this.bodyScrollState.htmlOverflow;

    this.bodyScrollState = null;
    window.scrollTo(0, scrollY);
  }

  open() {
    if (!this.modal) return;
    if (this.modal.hasAttribute('open')) return;

    const isMobile = this.isMobileViewport();

    if (isMobile) {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }

    if (isMobile) {
      this.modal.show();
      this.usesTopLayerDialog = false;
    } else {
      this.modal.showModal();
      this.usesTopLayerDialog = true;
    }

    this.lockPageScroll();
    
    this.setModalPosition();
    if (this.modalContent) {
      this.modalContent.scrollTop = 0;
    }
    this.moveSwymNotificationsIntoModal();
    this.setupSwymObserver();
    this.updateSirvSource(this.getCurrentVariantId());
    
    setTimeout(() => {
      if (window.Sirv && this.sirvContainer) {
        const instance = window.Sirv.getInstance(this.sirvContainer);
        if (instance && instance.viewer) {
          instance.viewer.resize();
        } else {
          window.Sirv.start(this.sirvContainer);
        }
      }
    }, 100);
    
    this.dispatchEvent(new CustomEvent('modal:opened'));
  }

  close() {
    if (this.modal?.hasAttribute('open')) {
      this.modal.close();
    }

    if (this.modalContent) {
      this.modalContent.scrollTop = 0;
    }
    this.unlockPageScroll();
    this.waitingForCart = false;
    this.usesTopLayerDialog = false;
    this.disconnectSwymObserver();
    this.restoreSwymNotificationsContainer();

    this.dispatchEvent(new CustomEvent('modal:closed'));
  }

  disconnectedCallback() {
    this.unlockPageScroll();
    this.usesTopLayerDialog = false;
    this.disconnectSwymObserver();
    this.restoreSwymNotificationsContainer();
    if (this.sectionRoot && this.boundDelegatedOpenHandler) {
      this.sectionRoot.removeEventListener('click', this.boundDelegatedOpenHandler);
    }
    window.removeEventListener('resize', this.boundResizeHandler);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.boundVisualViewportHandler);
      window.visualViewport.removeEventListener('scroll', this.boundVisualViewportHandler);
    }
    document.removeEventListener('cart:item-added', this.cartAddHandler);
    document.removeEventListener('cart:item-error', this.cartErrorHandler);
    
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
  }
}

customElements.define('three-sixty-modal', ThreeSixtyModal);